import React, { useMemo, useState } from 'react'
import { Card, Container, Row } from 'react-bootstrap'
import {
  getIntegrationServiceIdsForCategory,
  IntegrationCategory,
  IntegrationServiceId,
  isIntegrationOnlyDisconnect,
  isIntegrationUnConnectable,
} from '../../../common/integrations'
import { Button } from '../../Button/Button'
import './IntegrationCard.scss'
import { IntegrationIcon } from '../../IntegrationIcon'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import { IntegrationConnection } from '../../../views/Integration/Connectors/IntegrationConnection'
import { IntegrationCardDescription } from './IntegrationCardDescription'
import { LinkButton } from '../../Button/LinkButton'
import { useZocksAuth } from '../../ScopedBlock/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { IntegrationState } from '../IntegrationState'
import { IntegrationSpecificSetting } from '../../../views/Integration/IntegrationManagement/IntegrationSpecificSettings/IntegrationSpecificSetting'

interface ManageButtonProps {
  isDisabled?: boolean
  to: string
}

const ManageButton: React.FC<ManageButtonProps> = ({ isDisabled = false, to }) => {
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    if (isDisabled) {
      e.preventDefault()
    }
  }

  return (
    <div className='d-flex' onClick={stopPropagation} onContextMenu={stopPropagation} onMouseDown={stopPropagation} onAuxClick={stopPropagation}>
      <LinkButton variant='primaryGhost' className='w-100' disabled={isDisabled} to={to}>
        Manage
      </LinkButton>
    </div>
  )
}

interface ConnectButtonProps {
  onClick: () => void
  isDisabled?: boolean
}

const ConnectButton: React.FC<ConnectButtonProps> = ({ onClick, isDisabled = false }) => {
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <div className='d-flex' onClick={stopPropagation} onContextMenu={stopPropagation} onMouseDown={stopPropagation} onAuxClick={stopPropagation}>
      <Button variant='primary' className='w-100' onClick={onClick} disabled={isDisabled}>
        Connect
      </Button>
    </div>
  )
}

export interface IntegrationCardProps {
  serviceId: IntegrationServiceId
}

export const IntegrationCard: React.FC<IntegrationCardProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isLoading: userIsLoading } = useGetMeQuery({ accountId: accountId })
  const { hasScope } = useZocksAuth()
  const hasIntegrationConnectScope = useMemo(() => {
    return hasScope('**', 'integration:connect', ['integration:admin'])
  }, [hasScope])
  const [searchParams] = useSearchParams()

  const navigate = useNavigate()

  const currentIntegration = useMemo(() => {
    return userData?.integrations?.find((i) => (i.serviceId as IntegrationServiceId) === serviceId)
  }, [userData, serviceId])

  const hasIntegration: boolean = useMemo(() => {
    return !!currentIntegration
  }, [currentIntegration])

  const disableBecauseOtherMailIntegration = useMemo(() => {
    const mailIntegrationServiceIds = getIntegrationServiceIdsForCategory(IntegrationCategory.MAIL)
    if (!mailIntegrationServiceIds.includes(serviceId)) {
      return false
    }
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.type === 'mail' && i.serviceId !== serviceId))
  }, [userData, serviceId])

  const disableConnectButton = useMemo(() => {
    return disableBecauseOtherMailIntegration || !hasIntegrationConnectScope
  }, [disableBecauseOtherMailIntegration, hasIntegrationConnectScope])

  const disableManageButton = useMemo(() => {
    return !hasIntegrationConnectScope
  }, [hasIntegrationConnectScope])

  const hasSettingsSections = useMemo(() => IntegrationSpecificSetting({ serviceId }), [serviceId])

  const manageButtonTo =
    hasSettingsSections.length > 0 ? `/account/integration-settings/${serviceId}/settings` : `/account/integration-settings/${serviceId}/about`

  const [startConnect, setStartConnect] = useState<boolean>(() => {
    const crm = searchParams.get('crm')
    const connector = searchParams.get('connector')
    const validCRM = crm && Object.values<string>(IntegrationServiceId).includes(crm) ? (crm as IntegrationServiceId) : null
    const validConnector = connector && Object.values<string>(IntegrationServiceId).includes(connector) ? (connector as IntegrationServiceId) : null
    const shouldStartConnect = validCRM === serviceId || validConnector === serviceId
    if (shouldStartConnect && serviceId === IntegrationServiceId.OUTLOOK_CALENDAR && searchParams.get('connect') !== 'true') {
      return false
    }
    return shouldStartConnect
  })

  const onConnected = () => {
    setStartConnect(false)
  }

  const handleCardClick = () => {
    navigate(`/account/integration-settings/${serviceId}/about`)
  }

  if (userIsLoading) {
    return <div className='skeleton' style={{ width: '100%', height: '60px' }} />
  }

  const hideConnectOrManageButton = isIntegrationUnConnectable(serviceId) || isIntegrationOnlyDisconnect(serviceId)

  return (
    <>
      <IntegrationConnection serviceId={serviceId} startConnect={startConnect} onConnected={onConnected} />
      <Card className='integration-card rounded-3' onClick={handleCardClick}>
        <Card.Body className='p-3'>
          <Container className='integration-card__body-wrapper p-0'>
            <div className='rounded integration-card__header justify-content-between'>
              <IntegrationIcon serviceId={serviceId} className='integration-settings-icon' />
            </div>
            <Row>
              <IntegrationCardDescription serviceId={serviceId} />
            </Row>
            <IntegrationState currentIntegration={currentIntegration} className='d-flex flex-row align-items-start gap-1 mt-3 ' />
            {hideConnectOrManageButton ? null : (
              <Row className='integration-card__footer'>
                <Row className='integration-card__footer'>
                  {hasIntegration ? (
                    <ManageButton to={manageButtonTo} isDisabled={disableManageButton} />
                  ) : (
                    <ConnectButton onClick={() => setStartConnect(true)} isDisabled={disableConnectButton} />
                  )}
                </Row>
              </Row>
            )}
          </Container>
        </Card.Body>
      </Card>
    </>
  )
}
